import { ApiProvider, type ApiClient } from '@esnafai/shared/api';
import { useSession } from '@esnafai/shared/auth';
import { LiveProvider } from '@esnafai/shared/live';
import type { PushTransport } from '@esnafai/shared/push';
import { ToastProvider } from '@esnafai/shared/ui';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { api as appApi, queryClient as appQueryClient, streamUrl } from '@/lib/api';
import { PushProvider } from '@/lib/push';
import { router as appRouter, type AppRouter } from '@/lib/router';

// One stream per app session, closed while nobody is signed in (UI architecture §5).
function SessionLive({ children }: { children: ReactNode }) {
  const { principal } = useSession();
  return (
    <LiveProvider url={streamUrl} enabled={principal !== null}>
      {children}
    </LiveProvider>
  );
}

export function App({
  router = appRouter,
  queryClient = appQueryClient,
  api = appApi,
  pushTransport,
}: {
  router?: AppRouter;
  queryClient?: QueryClient;
  api?: ApiClient;
  pushTransport?: PushTransport;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider client={api}>
        <ToastProvider>
          <PushProvider transport={pushTransport}>
            <SessionLive>
              <RouterProvider router={router} />
            </SessionLive>
          </PushProvider>
        </ToastProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}
